import { Component, DestroyRef, inject, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogClose } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { AggregationOperatorLabels, AggregationOperatorsEnum } from '../../../../models/general.models';
import { distinctUntilChanged } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { parseCaseToWords } from '../../../../utils';
import { MatFormField, MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { MatButton } from '@angular/material/button';

@Component({
  selector: 'frmg-aggregation-modal-dialog',
  templateUrl: './aggregation-modal-dialog.component.html',
  styleUrls: ['./aggregation-modal-dialog.component.scss'],
  imports: [ReactiveFormsModule, MatFormField, MatSelect, MatOption, MatInput, MatRadioGroup, MatRadioButton, MatButton, MatDialogClose]
})
export class AggregationModalDialogComponent {
  public isLoading = false;
  public aggregationFG: FormGroup;
  public metadataKeys = [];

  public aggregationOperatorsEnum = Object.values(AggregationOperatorsEnum);
  public aggregationOperators = Object.values(AggregationOperatorsEnum);
  public aggregationOperatorLabels = AggregationOperatorLabels;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private dialogRef: MatDialogRef<AggregationModalDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder) {
    this.metadataKeys = [...this.data.exposure_fields, ...this.data.metadata_fields];
    this.aggregationFG = this.fb.group({
      field_path: [null, [Validators.required]],
      aggregation_function: [null, [Validators.required]],
      name: ['', [Validators.required]],
      unit: [''],
      unit_before: [true]
    });
    this.aggregationFG.get('field_path').valueChanges.pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef)).subscribe(field => {
      this.getAggregation(field.data_type);
    });
    this.aggregationFG.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value: {field_path: {name: string}, aggregation_function: AggregationOperatorsEnum, name: string}) => {
      if (this.aggregationFG.get('name').pristine && value.field_path && value.aggregation_function && !value.name) {
        this.aggregationFG.patchValue({
          name: `${AggregationOperatorLabels[value.aggregation_function]} ${this.columnHeader(value.field_path.name)}`
        });
      }
    });
  }

  public getAggregation(type: string) {
    this.aggregationOperators =  this.aggregationOperatorsEnum.filter(agg => {
      if (type === 'string') {
        return agg === AggregationOperatorsEnum.COUNT;
      } else {
        return true;
      }
    });
  }

  public closeDialog(result: any): void {
    this.dialogRef.close(result);
  }

  public create() {
    const values = this.aggregationFG.value;
    values.field_type = this.data.exposure_fields.includes(values.field_path) ? 'exposure' : 'metadata';
    values.field_path = values.field_path.name;
    values.description = `${values.name} (${values.unit.trim()})`;
    values.decimal_places = 0;
    if (!values.unit) {
      delete values.unit;
    }
    this.dialogRef.close(values);
  }

  public columnHeader(column: string) {
    switch (column) {
      case 'name':
        return 'Area Name';
      case 'avg_geometry_value':
        return 'Exposure';
      case 'geometry_type':
        return 'AOI Type';
      case 'risk_percentage':
        return 'Risk Percentage';
      case 'action':
        return '';
      default:
        return parseCaseToWords(column.replace('metadata.', ''));
    }
  }
}
